/* stolzausholz.de – SEO Blogartikel Pro */
(() => {
  const rootSelector = '.seo-article[data-seo-article]';

  const initializeArticle = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const cleanup = [];
    const content = root.querySelector('[data-article-content]');
    const articleMain = root.querySelector('[data-article-main]');
    const toc = root.querySelector('[data-toc]');
    const tocList = root.querySelector('[data-toc-list]');
    const progress = root.querySelector('[data-reading-progress]');
    const shareTitle = root.dataset.shareTitle || document.title;
    const shareText = root.dataset.shareText || '';
    const shareUrl = root.dataset.shareUrl || window.location.href;

    const slugify = (value) => {
      return value
        .trim()
        .toLowerCase()
        .replace(/ß/g, 'ss')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'abschnitt';
    };

    if (content) {
      content.querySelectorAll('img').forEach((image) => {
        if (!image.hasAttribute('loading')) image.loading = 'lazy';
        if (!image.hasAttribute('decoding')) image.decoding = 'async';
      });

      content.querySelectorAll('table').forEach((table) => {
        if (table.parentElement?.classList.contains('seo-article__table-scroll')) return;

        const wrapper = document.createElement('div');
        const caption = table.querySelector('caption')?.textContent.trim();
        wrapper.className = 'seo-article__table-scroll';
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', caption ? `Tabelle: ${caption}` : 'Tabelle – horizontal scrollbar');
        wrapper.tabIndex = 0;
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      });
    }

    if (content && toc && tocList) {
      const headings = [...content.querySelectorAll('h2, h3')].filter(
        (heading) => heading.textContent.trim().length > 0
      );
      const usedIds = new Set();

      headings.forEach((heading, index) => {
        let id = heading.id && heading.id.trim() ? heading.id.trim() : slugify(heading.textContent);
        const baseId = id;
        let duplicateIndex = 2;
        let existingElement = document.getElementById(id);

        while (usedIds.has(id) || (existingElement && existingElement !== heading)) {
          id = `${baseId}-${duplicateIndex}`;
          duplicateIndex += 1;
          existingElement = document.getElementById(id);
        }

        heading.id = id;
        usedIds.add(id);

        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${id}`;
        link.textContent = heading.textContent.trim();
        link.dataset.level = heading.tagName === 'H3' ? '3' : '2';
        link.dataset.tocIndex = String(index);
        item.appendChild(link);
        tocList.appendChild(item);
      });

      if (headings.length >= 2) {
        toc.hidden = false;
        const links = [...tocList.querySelectorAll('a')];

        const setActiveLink = (id) => {
          links.forEach((link) => {
            if (link.getAttribute('href') === `#${id}`) {
              link.setAttribute('aria-current', 'location');
            } else {
              link.removeAttribute('aria-current');
            }
          });
        };

        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver(
            (entries) => {
              const visible = entries
                .filter((entry) => entry.isIntersecting)
                .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

              if (visible[0]) setActiveLink(visible[0].target.id);
            },
            { rootMargin: '-18% 0px -68% 0px', threshold: [0, 1] }
          );

          headings.forEach((heading) => observer.observe(heading));
          cleanup.push(() => observer.disconnect());
        }

        setActiveLink(headings[0].id);
      }
    }

    if (progress && articleMain) {
      let ticking = false;

      const updateProgress = () => {
        const rect = articleMain.getBoundingClientRect();
        const start = window.scrollY + rect.top;
        const end = start + articleMain.offsetHeight - window.innerHeight;
        const range = Math.max(end - start, 1);
        const current = Math.min(Math.max((window.scrollY - start) / range, 0), 1);
        progress.style.transform = `scaleX(${current})`;
        ticking = false;
      };

      const requestProgressUpdate = () => {
        if (ticking) return;
        window.requestAnimationFrame(updateProgress);
        ticking = true;
      };

      updateProgress();
      window.addEventListener('scroll', requestProgressUpdate, { passive: true });
      window.addEventListener('resize', requestProgressUpdate, { passive: true });
      cleanup.push(() => window.removeEventListener('scroll', requestProgressUpdate));
      cleanup.push(() => window.removeEventListener('resize', requestProgressUpdate));
    }

    const nativeShare = root.querySelector('[data-native-share]');
    if (nativeShare && navigator.share) {
      nativeShare.hidden = false;
      nativeShare.addEventListener('click', async () => {
        try {
          await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        } catch (error) {
          if (error?.name !== 'AbortError') console.error(error);
        }
      });
    }

    const copyButton = root.querySelector('[data-copy-link]');
    if (copyButton) {
      copyButton.addEventListener('click', async () => {
        const status = copyButton.querySelector('[data-copy-status]');
        const successLabel = copyButton.dataset.labelSuccess || 'Kopiert';
        const defaultLabel = copyButton.dataset.labelDefault || 'Link kopieren';

        try {
          await navigator.clipboard.writeText(shareUrl);
          copyButton.setAttribute('aria-label', successLabel);
          if (status) status.textContent = successLabel;

          window.setTimeout(() => {
            copyButton.setAttribute('aria-label', defaultLabel);
            if (status) status.textContent = defaultLabel;
          }, 1800);
        } catch (error) {
          window.prompt(defaultLabel, shareUrl);
        }
      });
    }

    root.seoArticleCleanup = () => cleanup.forEach((callback) => callback());
  };

  const initializeWithin = (scope = document) => {
    if (scope.matches?.(rootSelector)) initializeArticle(scope);
    scope.querySelectorAll?.(rootSelector).forEach(initializeArticle);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
  } else {
    initializeWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(rootSelector)
      ? event.target
      : event.target.querySelector?.(rootSelector);
    root?.seoArticleCleanup?.();
  });
})();
